import React, { useState, Suspense, Fragment } from 'react';
import { Global, css } from '@emotion/react';
import { useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';

// shared-repos
import HamburgerIcon from '@zola/zola-ui/src/components/SvgIcons/Hamburger';
import HeartIcon from '@zola/zola-ui/src/components/SvgIcons/Heart';
import Notification from 'components/nav/TopNav/Notification';
import CartContainer from 'components/nav/TopNav/CartContainer';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';

// types
import type { RootState } from 'reducers/index';

// components
import MobileNavMenu from 'components/MobileNavV3/MobileNavMenu';
import UniversalSearch from 'components/search/UniversalSearch';
import RebrandLogo from '@zola/zola-ui/src/assets/images/rebrand/zola-logomark-marine.svg';
import RegistrySettingsDrawer from 'components/common/drawers/RegistrySettingsDrawer';

// util
import { isDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import { toggleDocumentOverlayClass } from 'util/overlayHelper';
import { isLoggedIn } from 'util/authHelpers';
import { getUserContext } from 'selectors/user';
import { getFavoritesLink } from 'util/getFavoritesLink';

import {
  Nav,
  TopNavBar,
  StyledNavLogo,
  HamburgerBtn,
  SharedIconStyles,
  SharedIconStylesWithBadge,
  UniversalSearchContainer,
} from './MobileNavV3.styles';
import '../../stylesheets/nav/MobileNavContainer.less';
import MobileNavContext from './MobileNavContext';

type MobileNavProps = {
  primaryLinkId: PrimaryNavIdentifier;
};

const MobileNav = ({ primaryLinkId }: MobileNavProps): JSX.Element => {
  const userContext = useSelector((state: RootState) => getUserContext(state));
  const userContextExists = userContext && !_isEmpty(userContext);
  const { object_id: userObjectId } = userContext;

  // by instanstiating as undefined, it prevents the animation on initial render; all subsequent updates will be boolean
  const [isMenuOpen, setIsMenuOpen] = useState<boolean | undefined>(undefined);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    toggleDocumentOverlayClass(!isMenuOpen, 'mobile-nav-v3--active');
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  if (!userContextExists || isDesktop()) return <></>;
  return (
    <Nav>
      <MobileNavContext.Provider value={{ primaryLinkId }}>
        <TopNavBar>
          <StyledNavLogo logo={RebrandLogo} />
          {isLoggedIn(userContext) && (
            <Fragment>
              {/* https://emotion.sh/docs/typescript explains why we have to use Fragment */}
              <a
                css={SharedIconStyles}
                href={getFavoritesLink(primaryLinkId)}
                data-testid="mobile-nav-favorite-link"
              >
                <HeartIcon width={24} height={24} />
              </a>
              {userObjectId && (
                <Notification css={SharedIconStylesWithBadge} userId={userObjectId} isMobileNavV3 />
              )}
            </Fragment>
          )}
          <CartContainer css={SharedIconStylesWithBadge} isNavV2 />
          <HamburgerBtn css={SharedIconStyles} onClick={toggleMenu}>
            <HamburgerIcon width={24} height={24} />
          </HamburgerBtn>
          {isLoggedIn(userContext) && <RegistrySettingsDrawer />}
        </TopNavBar>
        <MobileNavMenu
          isMenuOpen={isMenuOpen}
          toggleSearch={toggleSearch}
          toggleMenu={toggleMenu}
        />
        {isSearchOpen && (
          <Suspense fallback={<div>loading search result</div>}>
            <UniversalSearchContainer>
              <UniversalSearch activeLinkId={primaryLinkId} onCloseSearch={toggleSearch} />
            </UniversalSearchContainer>
          </Suspense>
        )}
      </MobileNavContext.Provider>
      <Global
        styles={css`
          .root-nav {
            z-index: 40;
          }
        `}
      />
    </Nav>
  );
};

export default MobileNav;
